// =====================================================================
// EditPaymentDialog (S103) — corrige um pagamento JÁ LANÇADO: valor, forma de
// pagamento, parcelas (crédito), pagador e o comprovante da maquininha. Pedido do
// dono: a recepção às vezes erra o valor (1,60 em vez de 1.600), a forma, ou esquece
// de anexar o comprovante — deixar corrigir depois de lançado. A correção de
// valor/forma CASCATEIA no backend pro caixa/cartão/comissão (estorna e re-lança);
// aqui é só a UI. Gated por tenant (o botão ✏️ só aparece se canEdit). Padrão MUI v4,
// espelha o QuickPaymentDialog.
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Typography, Box, CircularProgress, Divider
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  updatePayment, uploadPaymentReceipt, deletePaymentReceipt, openPaymentReceipt,
  PAYMENT_METHODS, CARD_BRANDS, financeErrorMessage
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const EditPaymentDialog = ({ open, onClose, onSaved, payment }) => {
  const isMobile = useIsMobile();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("dinheiro");
  const [cardBrand, setCardBrand] = useState("visa");
  const [cardInstallments, setCardInstallments] = useState("1");
  const [payerName, setPayerName] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);      // novo arquivo a subir
  const [removeReceipt, setRemoveReceipt] = useState(false);  // marcar o atual p/ remover
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !payment) return;
    setAmount(payment.amount != null ? String(payment.amount) : "");
    setMethod(payment.method || "dinheiro");
    setCardBrand("visa");
    setCardInstallments(payment.installmentsTotal > 1 ? String(payment.installmentsTotal) : "1");
    setPayerName(payment.payerName || "");
    setReceiptFile(null);
    setRemoveReceipt(false);
    setSaving(false);
  }, [open, payment]);

  const isCard = method === "debito" || method === "credito";
  const hasReceipt = !!(payment && payment.receiptPath);

  const handleSave = async () => {
    if (!payment) return;
    const value = Number(String(amount).replace(",", "."));
    if (!value || value <= 0) { toast.error("Informe um valor válido."); return; }
    setSaving(true);
    try {
      const payload = {
        amount: value,
        method,
        payerName: payerName || "" // "" limpa o pagador
      };
      if (isCard) {
        payload.cardBrand = cardBrand;
        if (method === "credito") payload.cardInstallments = Number(cardInstallments) || 1;
      }
      await updatePayment(payment.id, payload);
      // Comprovante (independente do valor/forma): sobe o novo OU remove o atual.
      if (receiptFile) {
        try { await uploadPaymentReceipt(payment.id, receiptFile); }
        catch (e) { toast.warn("Alterações salvas, mas o comprovante não subiu. Tente anexar de novo."); }
      } else if (removeReceipt && hasReceipt) {
        try { await deletePaymentReceipt(payment.id); }
        catch (e) { toast.warn("Alterações salvas, mas não consegui remover o comprovante."); }
      }
      toast.success("Pagamento corrigido. ✅");
      onSaved && onSaved();
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openCurrent = () =>
    openPaymentReceipt(payment.id).catch(() => toast.error("Não foi possível abrir o comprovante."));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Corrigir pagamento</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {payment && payment.patient && payment.patient.fullName ? `Paciente: ${payment.patient.fullName}. ` : ""}
          Corrigir o valor ou a forma ajusta o caixa automaticamente.
        </Typography>

        <Box display="flex" style={{ gap: 12, flexWrap: "wrap", marginTop: 4 }}>
          <TextField
            label="Valor"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            margin="dense"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            helperText="Use ponto pros centavos (ex.: 1600.00 = mil e seiscentos)"
            style={{ flexGrow: 1, minWidth: 150 }}
          />
          <TextField
            select
            label="Forma de pagamento"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            margin="dense"
            style={{ flexGrow: 1, minWidth: 180 }}
          >
            {PAYMENT_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
        </Box>

        {isCard && (
          <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
            <TextField select label="Bandeira" value={cardBrand} onChange={(e) => setCardBrand(e.target.value)}
              margin="dense" style={{ flexGrow: 1, minWidth: 130 }}>
              {CARD_BRANDS.filter((b) => b.value !== "todas").map((b) => (
                <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
              ))}
            </TextField>
            {method === "credito" && (
              <TextField label="Parcelas" type="number" value={cardInstallments}
                onChange={(e) => setCardInstallments(e.target.value)} margin="dense"
                inputProps={{ min: 1, max: 24 }} style={{ flexGrow: 1, minWidth: 130 }} />
            )}
          </Box>
        )}

        <TextField
          label="Quem pagou? (opcional, se diferente do paciente)"
          value={payerName}
          onChange={(e) => setPayerName(e.target.value)}
          fullWidth
          margin="dense"
          placeholder="Ex.: cônjuge/familiar que passou o cartão"
        />

        <Box mt={2} mb={1}><Divider /></Box>

        <Typography variant="body2" color="textSecondary" gutterBottom>
          Comprovante da maquininha
        </Typography>

        {hasReceipt && !receiptFile && !removeReceipt && (
          <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" onClick={openCurrent}>👁️ Ver atual</Button>
            <Button component="label" size="small" variant="outlined">
              Trocar
              <input type="file" accept="image/*,application/pdf" hidden
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; setReceiptFile(f || null); }} />
            </Button>
            <Button size="small" style={{ color: "#c62828" }} onClick={() => setRemoveReceipt(true)}>Remover</Button>
          </Box>
        )}

        {(!hasReceipt || removeReceipt) && !receiptFile && (
          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
            <Button component="label" size="small" variant="outlined">
              📎 Anexar comprovante
              <input type="file" accept="image/*,application/pdf" hidden
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; setReceiptFile(f || null); setRemoveReceipt(false); }} />
            </Button>
            {removeReceipt && (
              <Typography variant="caption" style={{ color: "#c62828" }}>
                comprovante atual será removido ao salvar — <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setRemoveReceipt(false)}>desfazer</span>
              </Typography>
            )}
          </Box>
        )}

        {receiptFile && (
          <Typography variant="caption">
            {receiptFile.name} — <span style={{ color: "#c62828", cursor: "pointer" }} onClick={() => setReceiptFile(null)}>remover</span>
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}>
          Salvar alterações
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPaymentDialog;
